import { useNavigate } from "react-router-dom";
import { Box, TextField, Button, Alert, Stack, Typography } from "@mui/material";
import PageTitle from "../components/PageTitle";
import { useState } from "react";
import { api, GameVersionInput } from "../../shared/api/client";
import BoxCard from "../../shared/components/BoxCard";
import useUserId from "../../shared/hooks/useUserId";

export default function GameCreate() {
  const [uid] = useUserId();
  const navigate = useNavigate();
  const [configJson, setConfigJson] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!uid) {
    return null;
  }

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const config = JSON.parse(configJson);

      // Validate required fields
      if (!config.id) {
        throw new Error("Game id is required");
      }

      // Build the initialVersion from the config
      const initialVersion: GameVersionInput = {
        version: config.version,
        name: config.name,
        description: config.description || "",
        major: config.major,
        maxPlayers: config.maxPlayers,
        minPlayers: config.minPlayers,
        webEntry: config.webEntry,
        nodeEntry: config.nodeEntry,
        backgroundColor: config.backgroundColor || "#000000",
        foregroundColor: config.foregroundColor || "#ffffff",
        boxArt: config.boxArt,
        screenshot: config.screenshot,
        licenseInformation: config.licenseInformation,
        implementationNotes: config.implementationNotes,
        publisher: config.publisher,
        designer: config.designer,
        artist: config.artist,
        tutorialVideoUrl: config.tutorialVideoUrl,
        rulesPdfUrl: config.rulesPdfUrl,
        purchaseUrl: config.purchaseUrl,
        publisherUrl: config.publisherUrl,
        repositoryUrl: config.repositoryUrl,
        configurationOptions: config.configurationOptions,
      };

      const { game } = await api.createGame({
        id: config.id,
        initialVersion,
      });
      navigate(`/game/${game.id}`);
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError("Invalid JSON format. Please check the syntax.");
      } else {
        setError(err instanceof Error ? err.message : "Failed to create game");
      }
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageTitle title="New Game" />

      <BoxCard title="Game Configuration">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste the game configuration JSON. The game ID and initial version will be created.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          value={configJson}
          onChange={(e) => setConfigJson(e.target.value)}
          multiline
          minRows={10}
          maxRows={20}
          fullWidth
          placeholder={`{
  "id": "my-game",
  "name": "My Game",
  "version": "1.0",
  "major": 1,
  "description": "Game description",
  "maxPlayers": 4,
  "minPlayers": 2,
  "webEntry": "https://...",
  "nodeEntry": "https://...",
  "backgroundColor": "#000000",
  "foregroundColor": "#ffffff",
  "boxArt": "https://...",
  "screenshot": "https://..."
}`}
          sx={{ mb: 2, fontFamily: "monospace", fontSize: "0.875rem" }}
        />

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !configJson.trim()}
          >
            {saving ? "Creating..." : "Create Game"}
          </Button>
        </Stack>
      </BoxCard>
    </Box>
  );
}
